<script lang="ts">

import IconComponent from "@/components/atoms/Icon.vue";
import { SearchResultItem } from "@/domain/main-header/entity/SearchResultItem.interface";
import { Component, Prop, Vue } from "vue-facing-decorator";
import { SearchResponse, SearchSuggestion, SearchSuggestionOptions } from "webshop-api";
import { IconName, TranslationKey } from "webshop-modules";

@Component({
    components: {
        IconComponent
    }
})
export default class QuickSearch extends Vue {

    @Prop() public isActive!: boolean;
    @Prop() public searchParam!: string;
    @Prop() public searchUrl!: string;

    public readonly IconName: typeof IconName = IconName;
    public readonly Translation: typeof TranslationKey = TranslationKey;

    public searchString = '';
    public searchQueryResults: SearchResultItem[] | null = [];
    public searchInputChangeTimeout: number | null = null;
    public quickSearchLabel = '';

    public get searchResults(): SearchResultItem[] | null {
        if (this.searchQueryResults && this.searchQueryResults.length > 0) {
            const pattern = new RegExp(this.searchString, "gi");
            this.searchQueryResults.forEach((e: SearchResultItem) => {
                if (e.title) {
                    e.title = e.title.replace(pattern, '<strong>$&</strong>');
                }
            });

            return this.searchQueryResults;
        } else if (this.searchQueryResults) {
            return [];
        }

        return null;
    }

    public toggleSearch() {
        if (!this.isActive) {
            const searchInput: HTMLElement = this.$refs.searchInput as HTMLElement;
            searchInput.focus();
        } else {
            const searchForm: HTMLFormElement = this.$refs.searchForm as HTMLFormElement;
            searchForm.submit();
        }

        this.$emit('toggleSearch');
    }

    private updateSearchString(suggestion: string): void {
        this.searchQueryResults = null;
        this.searchString = suggestion;
        this.searchInputChange();
    }

    private searchInputChange(): void {
        if (this.searchInputChangeTimeout) {
            clearTimeout(this.searchInputChangeTimeout);
        }
        if (this.searchString.length > 1) {
            this.searchInputChangeTimeout = setTimeout(() => {
                this.updateSearchResults();
            }, 500);
        } else {
            this.searchQueryResults = null;
            this.$emit('updateSearch', this.searchString, this.searchResults);
        }
    }

    private updateSearchResults(): void {
        this.$apiService.search({searchText: this.searchString}).then((response: any) => {
            if (response.data) {
                const responseData: SearchResponse = response.data;

                if (responseData.results && responseData.results.length > 0) {
                    this.searchQueryResults = responseData.results;
                } else if (responseData.suggestions) {
                    const suggestions: string[] = [];
                    this.searchQueryResults = [];

                    responseData.suggestions.forEach((type: SearchSuggestion) => {
                        if (type.options && type.options.length) {
                            type.options.forEach((option: SearchSuggestionOptions) => {
                                if (option.text && suggestions.indexOf(option.text) === -1) {
                                    suggestions.push(option.text);
                                }
                            });
                        }
                    });

                    if (suggestions.length) {
                        suggestions.forEach((item: string) => {
                            if (this.searchQueryResults) {
                                this.searchQueryResults.push({
                                    title: item,
                                    type: 'search'
                                });
                            }
                        });
                    }
                } else {
                    this.searchQueryResults = [];
                }
            }

            this.$emit('updateSearch', this.searchString, this.searchResults);
        });
    }

    private async mounted() {
        this.quickSearchLabel = await this.$translationService.getTranslationFor(TranslationKey.Search_label);
    }

}
</script>

<template>
    <div class="o-mainHeader__quicksearch">
        <div class="o-mainHeader__quicksearchTrigger" @click="toggleSearch()">
            <IconComponent :icon-id="IconName.Search"/>
        </div>
        <form :class="['o-mainHeader__quicksearchForm', {'is-active': isActive}]"
            :action="searchUrl" method="get" ref="searchForm"
        >
            <input class="o-mainHeader__quicksearchInput"
                    type="text"
                    autocomplete="off"
                    :name="searchParam"
                    :aria-label="quickSearchLabel"
                    :value="searchString"
                    ref="searchInput"
                    placeholder=""
                    @input="e => { searchString = e.target.value; searchInputChange(); }">
        </form>
    </div>
</template>
